import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import {
  CheckCircle2,
  XCircle,
  ArrowRight,
  QrCode,
  Mail,
  MessageSquare,
  Globe,
  Sparkles,
  Smartphone,
} from "lucide-react";

export const NixlevConcept3: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const bgIvory = "#F8F6F0";
  const greenForest = "#1B3B2B";
  const goldAccent = "#D4A346";
  const textDark = "#121814";

  const SUBTITLES = [
    { from: 0, to: 160, text: "Bayangkan punya website jualan profesional tanpa pusing koding & server.", highlight: "tanpa pusing koding & server" },
    { from: 160, to: 240, text: "Itulah Nixlev.com.", highlight: "Nixlev.com" },
    { from: 240, to: 420, text: "Mengubah ide & keahlianmu jadi bisnis digital otomatis.", highlight: "bisnis digital otomatis" },
    { from: 420, to: 550, text: "Etalase minimalis kilat, dioptimalkan untuk smartphone.", highlight: "dioptimalkan untuk smartphone" },
    { from: 550, to: 720, text: "Terima pembayaran QRIS instan dari semua bank & e-wallet.", highlight: "QRIS instan" },
    { from: 720, to: 930, text: "Akses produk langsung terkirim otomatis ke Email & WhatsApp detik itu juga.", highlight: "ke Email & WhatsApp" },
    { from: 930, to: 1100, text: "Tanpa drama begadang cek mutasi. Tanpa komisi membingungkan.", highlight: "Tanpa drama begadang" },
    { from: 1100, to: 1320, text: "Ubah keahlianmu jadi cuan nyata di nixlev.com 🚀", highlight: "nixlev.com" },
  ];

  const currentSub = SUBTITLES.find((s) => frame >= s.from && frame < s.to);

  return (
    <AbsoluteFill
      style={{
        backgroundColor: frame >= 1100 ? greenForest : bgIvory,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif",
        color: frame >= 1100 ? "#FFFFFF" : textDark,
        overflow: "hidden",
        transition: "background-color 0.4s ease",
      }}
    >
      <Audio src={staticFile("nixlev_concept3_orus.mp3")} />

      {/* SFX */}
      {frame === 0 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.4} />}
      {frame === 160 && <Audio src={staticFile("chime.mp3")} volume={0.7} />}
      {frame === 420 && <Audio src={staticFile("click.mp3")} volume={0.6} />}
      {frame === 550 && <Audio src={staticFile("whoosh.mp3")} volume={0.5} />}
      {frame === 720 && <Audio src={staticFile("chime.mp3")} volume={0.8} />}
      {frame === 1100 && <Audio src={staticFile("chime.mp3")} volume={0.85} />}

      {/* INTRO: DISSOLVING TECHNICAL FRICTION (0 - 420) */}
      {frame < 420 && (
        <AbsoluteFill
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "80px 40px",
          }}
        >
          <div
            style={{
              padding: "10px 26px",
              backgroundColor: "rgba(27, 59, 43, 0.08)",
              borderRadius: 30,
              fontSize: 22,
              fontWeight: 800,
              color: greenForest,
              marginBottom: 30,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <Sparkles size={24} />
            Zero Friction Storefront
          </div>

          <h1 style={{ fontSize: 58, fontWeight: 900, textAlign: "center", lineHeight: 1.15, marginBottom: 40 }}>
            Website Jualan Profesional, <br />
            <span style={{ color: "#16A34A" }}>Tanpa Ribet Koding.</span>
          </h1>

          {/* Dissolving Obstacle Cards */}
          <div style={{ width: "100%", maxWidth: 840, display: "flex", flexDirection: "column", gap: 16 }}>
            {[
              "❌ Sewa Server & Hosting Mahal",
              "❌ Setup Database & Plugin Rumit",
              "❌ Birokrasi Payment Gateway",
            ].map((obs, i) => (
              <div
                key={i}
                style={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: 18,
                  padding: "18px 24px",
                  fontSize: 22,
                  fontWeight: 700,
                  color: "#6B7280",
                  border: "2px solid #E5E7EB",
                  boxShadow: "0 10px 25px rgba(0,0,0,0.04)",
                  textDecoration: frame >= 120 ? "line-through" : "none",
                  opacity: frame >= 120 ? 0.6 : 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>{obs}</span>
                {frame >= 120 && <XCircle size={28} color="#EF4444" />}
              </div>
            ))}
          </div>
        </AbsoluteFill>
      )}

      {/* SHOWCASE: DESKTOP & SMARTPHONE OPTIMIZATION (420 - 720) */}
      {frame >= 420 && frame < 720 && (
        <AbsoluteFill
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "70px 40px",
          }}
        >
          <div
            style={{
              padding: "10px 24px",
              backgroundColor: "#DCFCE7",
              color: "#15803D",
              borderRadius: 30,
              fontSize: 22,
              fontWeight: 800,
              marginBottom: 20,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <Smartphone size={24} />
            Dioptimalkan Untuk Smartphone
          </div>

          <h2 style={{ fontSize: 50, fontWeight: 900, marginBottom: 30, textAlign: "center" }}>
            Etalase Cepat & Responsif
          </h2>

          <div
            style={{
              width: 940,
              height: 520,
              backgroundColor: "#FFFFFF",
              borderRadius: 24,
              border: "1px solid #E5E7EB",
              boxShadow: "0 25px 60px rgba(0,0,0,0.12)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                height: 46,
                backgroundColor: "#F9FAFB",
                borderBottom: "1px solid #E5E7EB",
                display: "flex",
                alignItems: "center",
                padding: "0 18px",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#FF5F56" }} />
                <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#FFBD2E" }} />
                <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#27C93F" }} />
              </div>
              <div
                style={{
                  flex: 1,
                  height: 30,
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #E5E7EB",
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 15,
                  fontWeight: 600,
                  color: "#374151",
                  gap: 8,
                }}
              >
                <Globe size={15} color="#10B981" />
                https://nixlev.com
              </div>
            </div>
            <Img
              src={staticFile("nixlev_desktop.png")}
              style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center" }}
            />
          </div>
        </AbsoluteFill>
      )}

      {/* CHECKOUT & INSTANT DUAL DISPATCH (720 - 1100) */}
      {frame >= 720 && frame < 1100 && (
        <AbsoluteFill
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "70px 40px",
          }}
        >
          <div
            style={{
              padding: "10px 24px",
              backgroundColor: "#DCFCE7",
              color: "#15803D",
              borderRadius: 30,
              fontSize: 22,
              fontWeight: 800,
              marginBottom: 20,
            }}
          >
            QRIS Instan + Dual Delivery
          </div>

          <h2 style={{ fontSize: 52, fontWeight: 900, marginBottom: 35, textAlign: "center" }}>
            Terkirim Detik Itu Juga
          </h2>

          <div
            style={{
              width: 360,
              backgroundColor: "#111827",
              borderRadius: 28,
              padding: "24px 20px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              boxShadow: "0 25px 60px rgba(0,0,0,0.25)",
              marginBottom: 25,
            }}
          >
            <div style={{ width: 180, height: 180, backgroundColor: "#FFF", borderRadius: 16, padding: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <QrCode size={160} color="#111827" />
            </div>
            <div style={{ marginTop: 14, fontSize: 18, fontWeight: 800, color: "#10B981" }}>
              Semua Bank & E-Wallet
            </div>
          </div>

          {/* Dual Delivery Stack */}
          <div style={{ width: "100%", maxWidth: 840, display: "flex", gap: 16 }}>
            <div
              style={{
                flex: 1,
                backgroundColor: "#FFFFFF",
                borderRadius: 20,
                padding: "20px 24px",
                border: "2px solid #22C55E",
                boxShadow: "0 10px 25px rgba(34, 197, 94, 0.12)",
                display: "flex",
                alignItems: "center",
                gap: 16,
              }}
            >
              <MessageSquare size={34} color="#15803D" />
              <div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#14532D" }}>WhatsApp Otomatis</div>
                <div style={{ fontSize: 15, color: "#4B5563" }}>Link download langsung masuk chat.</div>
              </div>
            </div>

            <div
              style={{
                flex: 1,
                backgroundColor: "#FFFFFF",
                borderRadius: 20,
                padding: "20px 24px",
                border: "2px solid #3B82F6",
                boxShadow: "0 10px 25px rgba(59, 130, 246, 0.12)",
                display: "flex",
                alignItems: "center",
                gap: 16,
              }}
            >
              <Mail size={34} color="#2563EB" />
              <div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#1E3A8A" }}>Email Invoice</div>
                <div style={{ fontSize: 15, color: "#4B5563" }}>Lisensi & arsip resmi aman.</div>
              </div>
            </div>
          </div>
        </AbsoluteFill>
      )}

      {/* OUTRO: GREEN THEME NIXLEV (1100 - 1320) */}
      {frame >= 1100 && (
        <AbsoluteFill
          style={{
            backgroundColor: greenForest,
            color: "#FFFFFF",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "80px 40px",
          }}
        >
          {/* Logo with Emerald Glow */}
          <div
            style={{
              padding: 24,
              borderRadius: "50%",
              backgroundColor: "rgba(255, 255, 255, 0.12)",
              border: `3px solid ${goldAccent}`,
              boxShadow: "0 0 50px rgba(16, 185, 129, 0.45)",
              transform: `scale(${spring({
                frame: frame - 1100,
                fps,
                config: { damping: 14, stiffness: 120 },
              })})`,
              marginBottom: 25,
            }}
          >
            <Img src={staticFile("nixlev_logo.png")} style={{ width: 170, height: 170, objectFit: "contain" }} />
          </div>

          <h1 style={{ fontSize: 62, fontWeight: 900, textAlign: "center", lineHeight: 1.15, marginBottom: 25 }}>
            Ubah Keahlianmu <br />
            <span style={{ color: "#34D399" }}>Jadi Cuan Nyata.</span>
          </h1>

          <div
            style={{
              backgroundColor: "#10B981",
              color: "#FFFFFF",
              padding: "24px 54px",
              borderRadius: 32,
              fontSize: 32,
              fontWeight: 900,
              display: "flex",
              alignItems: "center",
              gap: 18,
              boxShadow: "0 20px 50px rgba(16, 185, 129, 0.4)",
            }}
          >
            <span>Buka Toko Gratis di nixlev.com</span>
            <ArrowRight size={36} color="#FFFFFF" />
          </div>
        </AbsoluteFill>
      )}

      {/* SUBTITLES SAFE ZONE */}
      {currentSub && (
        <div
          style={{
            position: "absolute",
            bottom: 220,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            padding: "0 40px",
            zIndex: 100,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              backgroundColor: "rgba(18, 24, 20, 0.90)",
              backdropFilter: "blur(12px)",
              padding: "16px 36px",
              borderRadius: 24,
              border: "1px solid rgba(255, 255, 255, 0.14)",
              boxShadow: "0 15px 35px rgba(0,0,0,0.35)",
              maxWidth: 920,
              textAlign: "center",
              transform: `scale(${spring({
                frame: frame - currentSub.from,
                fps,
                config: { damping: 14, stiffness: 180 },
              })})`,
            }}
          >
            <span style={{ fontSize: 30, fontWeight: 800, color: "#FFFFFF" }}>
              {currentSub.highlight ? (
                <>
                  {currentSub.text.split(currentSub.highlight)[0]}
                  <span style={{ color: frame >= 1100 ? "#34D399" : goldAccent }}>{currentSub.highlight}</span>
                  {currentSub.text.split(currentSub.highlight)[1]}
                </>
              ) : (
                currentSub.text
              )}
            </span>
          </div>
        </div>
      )}
    </AbsoluteFill>
  );
};
